import React, { useMemo, useState } from "react";

const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const isSameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const Calendar = ({ selectedDate, onSelect, minDate, maxDate, closedWeekday = 2 }) => {
  const today = startOfDay(new Date());
  const initial = selectedDate || today;
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());

  const min = startOfDay(minDate || today);
  const max = startOfDay(maxDate || new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000));

  const cells = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    // Monday-first grid: convert Sunday(0)..Saturday(6) to Mo(0)..So(6)
    const startOffset = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const grid = [];
    for (let i = 0; i < startOffset; i += 1) {
      const d = new Date(viewYear, viewMonth, 1 - (startOffset - i));
      grid.push({ date: d, otherMonth: true });
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      grid.push({ date: new Date(viewYear, viewMonth, d), otherMonth: false });
    }
    while (grid.length % 7 !== 0) {
      const last = grid[grid.length - 1].date;
      grid.push({
        date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
        otherMonth: true,
      });
    }
    return grid;
  }, [viewYear, viewMonth]);

  const canPrev = () => {
    const monthStart = new Date(viewYear, viewMonth, 1);
    return monthStart > new Date(min.getFullYear(), min.getMonth(), 1);
  };
  const canNext = () => {
    const monthEnd = new Date(viewYear, viewMonth + 1, 0);
    return monthEnd < max;
  };

  return (
    <div>
      <div className="calendar-nav">
        <button
          type="button"
          onClick={() => {
            const m = viewMonth - 1;
            if (m < 0) { setViewMonth(11); setViewYear((y) => y - 1); }
            else setViewMonth(m);
          }}
          disabled={!canPrev()}
        >
          ← Zurück
        </button>
        <span className="calendar-title">
          {MONTHS_DE[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          onClick={() => {
            const m = viewMonth + 1;
            if (m > 11) { setViewMonth(0); setViewYear((y) => y + 1); }
            else setViewMonth(m);
          }}
          disabled={!canNext()}
        >
          Weiter →
        </button>
      </div>

      <div className="calendar-grid">
        {WEEKDAYS_DE.map((d) => (
          <div className="calendar-head" key={d}>{d}</div>
        ))}
        {cells.map(({ date, otherMonth }, idx) => {
          const isOutOfRange = date < min || date > max;
          // JS getDay: 0=Sun..6=Sat. Tuesday = 2.
          const isClosed = date.getDay() === closedWeekday;
          const disabled = otherMonth || isOutOfRange || isClosed;
          const selected = isSameDay(selectedDate, date);
          return (
            <button
              key={idx}
              type="button"
              className={[
                "calendar-cell",
                otherMonth ? "is-other-month" : "",
                isClosed && !otherMonth ? "is-closed" : "",
                selected ? "is-selected" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => !disabled && onSelect(date)}
              disabled={disabled}
              title={isClosed ? "Dienstag ist Ruhetag" : undefined}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { toISODate };
export default Calendar;
